import { useMemo, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

const COLORES = ["#1D9E75","#534AB7","#E24B4A","#EF9F27","#4F81BD","#A855F7","#EC4899","#14B8A6","#F97316","#6B7280"];

const fmt = n => "$" + Math.round(n ?? 0).toLocaleString("es-CL");

export default function StockEvolucion({ datos }) {
  const [seleccion, setSeleccion] = useState(null); // null = top 5 por defecto

  const { ranking, evolucion, fechas } = datos || {};

  // Productos a mostrar en el gráfico: los top 5 o los seleccionados manualmente
  const topNombres = useMemo(() =>
    (ranking || []).slice(0, 5).map(r => r.nombre),
  [ranking]);

  const nombresGrafico = seleccion ?? topNombres;

  // Construir datos del gráfico: una fila por fecha, una columna por producto seleccionado
  const chartData = useMemo(() => {
    if (!evolucion || !fechas) return [];
    const map = {};
    fechas.forEach(f => { map[f] = { fecha: f.slice(5) }; }); // MM-DD

    evolucion.forEach(({ nombre, datos: puntos }) => {
      if (!nombresGrafico.includes(nombre)) return;
      puntos.forEach(({ fecha, stock }) => {
        if (map[fecha]) map[fecha][nombre] = stock;
      });
    });
    return Object.values(map).sort((a, b) => a.fecha > b.fecha ? 1 : -1);
  }, [evolucion, fechas, nombresGrafico]);

  const toggleProducto = nombre => {
    setSeleccion(prev => {
      const base = prev ?? topNombres;
      if (base.includes(nombre)) {
        const next = base.filter(n => n !== nombre);
        return next.length === 0 ? base : next;
      }
      return [...base, nombre];
    });
  };

  if (!ranking || ranking.length === 0) {
    return (
      <div style={{ padding: "2rem", color: "#888", fontSize: 14, textAlign: "center" }}>
        Aún no hay datos de stock. Carga el primer archivo desde la pestaña <strong>Cargar archivos</strong>.
      </div>
    );
  }

  const top1 = ranking[0];

  return (
    <div style={{ fontFamily: "system-ui, sans-serif" }}>

      {/* ── Hero: top 3 ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gap: 10, marginBottom: "1.25rem" }}>
        {ranking.slice(0, 3).map((r, i) => {
          const pct = r.stock_inicial > 0 ? Math.round((r.unidades_vendidas / r.stock_inicial) * 100) : 0;
          const color = COLORES[i];
          return (
            <div key={r.nombre} style={{
              background: i === 0 ? "#111" : "#fff",
              border: i === 0 ? "none" : "0.5px solid #e8e8e6",
              borderRadius: 14, padding: "1.25rem 1.4rem",
              borderTop: `4px solid ${color}`,
              color: i === 0 ? "#fff" : "#111",
            }}>
              <div style={{ fontSize: 10, color: i === 0 ? "#666" : "#aaa", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                #{i + 1} · {r.tipo || "Producto"}
              </div>
              <div style={{ fontSize: i === 0 ? 15 : 13, fontWeight: 600, marginBottom: "0.75rem", lineHeight: 1.3 }}>{r.nombre}</div>

              <div style={{ display: "flex", gap: 16, marginBottom: 10, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 9, color: i === 0 ? "#666" : "#aaa", marginBottom: 2 }}>Tasa venta</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color }}>{r.tasa_venta} <span style={{ fontSize: 11, fontWeight: 400 }}>u/día</span></div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: i === 0 ? "#666" : "#aaa", marginBottom: 2 }}>Vendidas</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{r.unidades_vendidas}</div>
                </div>
              </div>

              {/* Barra de consumo de stock */}
              <div style={{ height: 5, background: i === 0 ? "rgba(255,255,255,0.1)" : "#eee", borderRadius: 3, marginBottom: 6 }}>
                <div style={{ height: 5, borderRadius: 3, background: color, width: `${Math.min(pct, 100)}%`, transition: "width 0.4s" }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: i === 0 ? "#666" : "#bbb" }}>
                <span>Stock inicial: {r.stock_inicial}</span>
                <span>Actual: {r.stock_actual} ({pct}% consumido)</span>
              </div>

              {r.margen_bruto > 0 && (
                <div style={{ marginTop: 10, fontSize: 11, color: i === 0 ? "#1D9E75" : "#1D9E75", fontWeight: 500 }}>
                  Ganancia estimada: {fmt(r.ganancia_estimada)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Gráfico evolutivo ── */}
      <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1rem 1.1rem", marginBottom: "1rem" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: "0.85rem", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Evolución de stock
            </div>
            <div style={{ fontSize: 11, color: "#bbb", marginTop: 2 }}>
              Haz clic en un producto del ranking para activar / desactivar
            </div>
          </div>
        </div>

        {/* Filtros de producto */}
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: "0.85rem" }}>
          {(evolucion || []).map(({ nombre }, idx) => {
            const activo = nombresGrafico.includes(nombre);
            const color  = COLORES[idx % COLORES.length];
            return (
              <button
                key={nombre}
                onClick={() => toggleProducto(nombre)}
                style={{
                  fontSize: 10, padding: "3px 10px", borderRadius: 20, cursor: "pointer",
                  border: activo ? `1.5px solid ${color}` : "0.5px solid #ddd",
                  background: activo ? color + "18" : "transparent",
                  color: activo ? color : "#aaa",
                  fontWeight: activo ? 600 : 400,
                  transition: "all 0.15s",
                }}
              >
                {nombre}
              </button>
            );
          })}
        </div>

        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(136,135,128,0.15)" vertical={false} />
            <XAxis dataKey="fecha" tick={{ fontSize: 9, fill: "#888" }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 9, fill: "#888" }} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "0.5px solid #ddd" }} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {nombresGrafico.map((nombre, idx) => {
              const realIdx = (evolucion || []).findIndex(e => e.nombre === nombre);
              const color   = COLORES[realIdx >= 0 ? realIdx % COLORES.length : idx % COLORES.length];
              return (
                <Line
                  key={nombre}
                  dataKey={nombre}
                  type="monotone"
                  stroke={color}
                  strokeWidth={2}
                  dot={{ r: 3, fill: color }}
                  connectNulls
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ── Ranking completo ── */}
      <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1rem 1.1rem" }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.85rem" }}>
          Ranking — Top 10 productos con mayor caída de stock
        </div>

        {/* Cabecera */}
        <div style={{ display: "grid", gridTemplateColumns: "0.3fr 2fr 0.7fr 0.7fr 0.7fr 0.8fr 0.8fr 1fr 1fr", borderBottom: "0.5px solid #eee" }}>
          {["#", "Producto", "Tipo", "Stock ini.", "Stock act.", "Vendidas", "u/día", "Margen", "Ganancia est."].map(h => (
            <div key={h} style={{ fontSize: 10, color: "#aaa", padding: "5px 6px", fontWeight: 500 }}>{h}</div>
          ))}
        </div>

        {ranking.map((r, i) => {
          const color = COLORES[i % COLORES.length];
          const pct   = r.stock_inicial > 0 ? Math.round((r.unidades_vendidas / r.stock_inicial) * 100) : 0;
          return (
            <div
              key={r.nombre}
              style={{
                display: "grid",
                gridTemplateColumns: "0.3fr 2fr 0.7fr 0.7fr 0.7fr 0.8fr 0.8fr 1fr 1fr",
                borderBottom: i < ranking.length - 1 ? "0.5px solid #f5f5f4" : "none",
                background: i % 2 === 0 ? "transparent" : "#fafaf9",
                alignItems: "center",
              }}
            >
              <div style={{ padding: "10px 6px" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color, background: color + "18", borderRadius: 4, padding: "2px 6px" }}>{i + 1}</span>
              </div>
              <div style={{ padding: "10px 6px" }}>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{r.nombre}</div>
                {/* Mini barra */}
                <div style={{ height: 3, background: "#eee", borderRadius: 2, marginTop: 4, width: "80%" }}>
                  <div style={{ height: 3, borderRadius: 2, background: color, width: `${Math.min(pct, 100)}%` }} />
                </div>
              </div>
              <div style={{ fontSize: 11, padding: "10px 6px", color: "#888" }}>{r.tipo || "—"}</div>
              <div style={{ fontSize: 12, padding: "10px 6px", color: "#555" }}>{r.stock_inicial}</div>
              <div style={{ fontSize: 12, padding: "10px 6px", color: r.stock_actual <= 10 ? "#E24B4A" : "#555", fontWeight: r.stock_actual <= 10 ? 600 : 400 }}>{r.stock_actual}</div>
              <div style={{ fontSize: 12, padding: "10px 6px", fontWeight: 600, color }}>{r.unidades_vendidas}</div>
              <div style={{ fontSize: 12, padding: "10px 6px", fontWeight: 600, color }}>{r.tasa_venta}</div>
              <div style={{ fontSize: 12, padding: "10px 6px", color: "#555" }}>{r.margen_bruto > 0 ? fmt(r.margen_bruto) : "—"}</div>
              <div style={{ fontSize: 12, padding: "10px 6px", fontWeight: 500, color: r.ganancia_estimada > 0 ? "#1D9E75" : "#aaa" }}>
                {r.ganancia_estimada > 0 ? fmt(r.ganancia_estimada) : "—"}
              </div>
            </div>
          );
        })}

        <div style={{ fontSize: 10, color: "#ccc", marginTop: 10 }}>
          u/día = unidades vendidas por día · Ganancia est. = unidades vendidas × (precio sugerido − precio proveedor)
        </div>
      </div>
    </div>
  );
}
